import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
  ConfirmationNumberOutlined as TicketIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";
import { CommonTable, CommonInput } from "../../ui";
import { EXPLORER_LABEL, getExplorerAddressUrl } from "../../../../static/Constants";

/**
 * LotteryDetailTicketsTable Component
 * Displays the original "Bought By" and "Numbers" table with address utilities,
 * search filtering, pagination, and circular lottery number balls.
 */
const LotteryDetailTicketsTable = ({
  lotterylist = [],
  lotteryType,
  winningNumbers,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedIndex, setCopiedIndex] = useState(null);

  const handleCopy = (text, idx) => {
    if (!text) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedIndex(idx);
      setTimeout(() => setCopiedIndex(null), 2000);
    }
  };

  // Winning numbers lookup set for quick match highlighting
  const winningSet = useMemo(() => {
    if (!winningNumbers) return new Set();
    if (Array.isArray(winningNumbers)) {
      return new Set(winningNumbers.map(String));
    }
    if (typeof winningNumbers === "string") {
      if (winningNumbers.includes("-")) {
        return new Set(winningNumbers.split("-").map(String));
      }
      return new Set(winningNumbers.split("").map(String));
    }
    return new Set([String(winningNumbers)]);
  }, [winningNumbers]);

  // Filtered rows
  const filteredList = useMemo(() => {
    if (!searchQuery.trim()) return lotterylist;
    const query = searchQuery.toLowerCase().trim();
    return lotterylist.filter((item) => {
      const addrMatch = item.useraddress?.toLowerCase().includes(query);
      const ticketStr = Array.isArray(item.ticketbought)
        ? item.ticketbought.join(" ")
        : String(item.ticketbought || "");
      const ticketMatch = ticketStr.toLowerCase().includes(query);
      return addrMatch || ticketMatch;
    });
  }, [lotterylist, searchQuery]);

  const columns = [
    {
      id: "index",
      label: "#",
      width: 60,
      align: "center",
      render: (_, row, index) => (
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: colors.slate[400],
          }}
        >
          {index + 1}
        </Typography>
      ),
    },
    {
      id: "useraddress",
      label: "Bought By",
      render: (useraddress, row, index) => {
        const address = useraddress || row?.useraddress || "";
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "13px",
                fontWeight: 600,
                color: colors.slate[800],
              }}
            >
              {address
                ? `${address.slice(0, 10)}...${address.slice(-8)}`
                : "0x000...000"}
            </Typography>
            <Tooltip
              title={copiedIndex === index ? "Copied!" : "Copy Address"}
              arrow
            >
              <IconButton
                size="small"
                onClick={() => handleCopy(address, index)}
                sx={{
                  p: 0.5,
                  color:
                    copiedIndex === index
                      ? colors.emerald[600]
                      : colors.slate[400],
                  "&:hover": {
                    color: colors.primary.main,
                    bgcolor: colors.blue[50],
                  },
                }}
              >
                {copiedIndex === index ? (
                  <CheckIcon sx={{ fontSize: 16 }} />
                ) : (
                  <CopyIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
            {address && (
              <Tooltip title={EXPLORER_LABEL} arrow>
                <IconButton
                  size="small"
                  component="a"
                  href={getExplorerAddressUrl(address)}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    p: 0.5,
                    color: colors.slate[400],
                    "&:hover": {
                      color: colors.primary.main,
                      bgcolor: colors.blue[50],
                    },
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      id: "ticketbought",
      label: "Numbers",
      render: (ticketbought, row) => {
        const rawTicket =
          ticketbought !== undefined ? ticketbought : row?.ticketbought;
        let numbers = [];
        if (String(lotteryType) === "3") {
          numbers = String(rawTicket || "")
            .split("")
            .map(Number);
        } else if (Array.isArray(rawTicket)) {
          numbers = rawTicket;
        } else if (rawTicket !== undefined && rawTicket !== null && rawTicket !== "") {
          numbers = [rawTicket];
        }

        return (
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
          >
            {numbers.map((num, i) => {
              const isWinning = winningSet.has(String(num));
              return (
                <Box
                  key={i}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "13px",
                    background: isWinning
                      ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                      : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                    color: isWinning ? "#ffffff" : colors.slate[800],
                    border: `1px solid ${
                      isWinning ? "#34d399" : colors.slate[300]
                    }`,
                    boxShadow: isWinning
                      ? "0 2px 8px rgba(16, 185, 129, 0.4)"
                      : customShadows.cardFlat,
                    transition: "all 0.15s ease",
                  }}
                >
                  {num}
                </Box>
              );
            })}
          </Stack>
        );
      },
    },
  ];

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.background.paper,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: borderRadiusPx.lg,
              backgroundColor: colors.purple[50],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TicketIcon sx={{ fontSize: 20, color: colors.purple[600] }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: colors.slate[900],
                fontSize: "1.05rem",
              }}
            >
              Bought Tickets
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.slate[500], fontSize: "12px" }}
            >
              {lotterylist.length} total entries recorded on smart contract
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ width: { xs: "100%", sm: "280px" } }}>
          <CommonInput
            fullWidth
            placeholder="Search address or numbers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startAdornment={
              <SearchIcon
                sx={{ fontSize: 18, color: colors.neutral.lightGrey }}
              />
            }
          />
        </Box>
      </Stack>

      <Divider sx={{ mb: 2.5, borderColor: colors.dashboard.divider }} />

      <CommonTable
        columns={columns}
        rows={filteredList}
        pagination={true}
        rowsPerPageOptions={[5, 10, 25, 50]}
        defaultRowsPerPage={10}
        emptyMessage={
          searchQuery
            ? "No participants found matching your search keyword."
            : "No tickets have been purchased for this lottery yet."
        }
      />
    </Card>
  );
};

LotteryDetailTicketsTable.propTypes = {
  lotterylist: PropTypes.array,
  lotteryType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  winningNumbers: PropTypes.any,
};

export default LotteryDetailTicketsTable;
